import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { useSubscribe } from "@/contexts/RealtimeContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Download, RotateCcw, DollarSign, CheckCircle2, Clock, XCircle } from "lucide-react";

const STATUS_TONE = {
  paid: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  pending: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  initiated: "border-white/10 text-zinc-300",
  refunded: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  partially_refunded: "border-orange-500/40 text-orange-300 bg-orange-500/10",
  failed: "border-rose-500/40 text-rose-300 bg-rose-500/10",
  expired: "border-white/10 text-zinc-400",
};

const STATUS_ICON = { paid: CheckCircle2, pending: Clock, refunded: RotateCcw,
                     partially_refunded: RotateCcw, failed: XCircle, expired: XCircle };

export default function PaymentsAdminPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ paid_amount: 0, pending_amount: 0, refunded_amount: 0, count: 0 });
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [ptype, setPtype] = useState("all");

  const load = useCallback(async () => {
    if (!propertyId) return;
    const params = new URLSearchParams({ property_id: propertyId });
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (ptype !== "all") params.set("payment_type", ptype);
    try {
      const { data } = await api.get(`/admin/payments/transactions?${params}`);
      setItems(data.items || []);
      setTotals(data.totals || {});
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/PaymentsAdminPage.jsx: swallowed', err); }
  }, [propertyId, q, status, ptype]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useSubscribe(propertyId ? `property:${propertyId}` : null, ({ event }) => {
    if (event === "payment.completed" || event === "ticket.update") load();
  });

  const refund = async (tx) => {
    const remaining = (tx.amount || 0) - (tx.refund_amount || 0);
    const raw = window.prompt(`Refund amount (max $${remaining.toFixed(2)}):`, remaining.toFixed(2));
    if (raw == null) return;
    const amt = Number(raw);
    if (!amt || amt <= 0) return;
    const reason = window.prompt("Reason?", "") || "";
    try {
      await api.post(`/admin/payments/transactions/${tx.session_id}/refund`, { amount: amt, reason });
      toast.success("Refund issued");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const exportCsv = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/transactions.csv?property_id=${propertyId}`;
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) { toast.error("Export failed"); return; }
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("CSV downloaded");
  };

  const pTypes = useMemo(() => {
    const s = new Set(items.map((i) => i.payment_type).filter(Boolean));
    return Array.from(s);
  }, [items]);

  return (
    <div>
      <PageHeader
        eyebrow="Money"
        title="Payments"
        subtitle="Every transaction on this property — filter, refund, and export in one place."
        actions={
          <Button size="sm" variant="outline" className="border-white/10" onClick={exportCsv} data-testid="export-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <StatBig label="Paid MTD" value={`$${(totals.paid_amount || 0).toFixed(2)}`} tone="text-emerald-300" />
        <StatBig label="Pending" value={`$${(totals.pending_amount || 0).toFixed(2)}`} tone="text-amber-300" />
        <StatBig label="Refunded" value={`$${(totals.refunded_amount || 0).toFixed(2)}`} tone="text-rose-300" />
        <StatBig label="Transactions" value={totals.count ?? 0} />
      </div>

      <Card className="border-white/10 bg-card p-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="tx-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or memo" className="pl-9 bg-white/[0.02] border-white/10" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="tx-status" className="w-44 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ptype} onValueChange={setPtype}>
            <SelectTrigger data-testid="tx-type" className="w-52 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {pTypes.map((t) => <SelectItem key={t} value={t}>{t.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-white/10 bg-card overflow-hidden">
        <div className="max-h-[65vh] overflow-y-auto scroll-slim divide-y divide-white/5">
          {items.map((tx, idx) => {
            const Icon = STATUS_ICON[tx.payment_status] || DollarSign;
            const remaining = (tx.amount || 0) - (tx.refund_amount || 0);
            const rowKey = tx.id || tx.session_id || `row-${idx}`;
            return (
              <div key={rowKey} className="px-4 py-3 flex items-center gap-4" data-testid={`payment-${rowKey}`}>
                <div className="h-9 w-9 rounded-md border border-white/10 bg-white/[0.03] flex items-center justify-center">
                  <Icon className="h-4 w-4 text-zinc-400" />
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{tx.user_name}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{tx.user_email}</div>
                  </div>
                  <div className="text-sm text-zinc-300 capitalize">{tx.payment_type?.replace("_", " ")}</div>
                  <div className="text-[11px] text-zinc-500">{new Date(tx.created_at).toLocaleString()}</div>
                  <div className="flex items-center gap-2 justify-end">
                    <Badge variant="outline" className={STATUS_TONE[tx.payment_status] || "border-white/10"}>{tx.payment_status}</Badge>
                    <div className="text-right">
                      <div className="tabular-nums">${tx.amount?.toFixed(2)}</div>
                      {tx.refund_amount ? <div className="text-[10px] text-rose-300">−${tx.refund_amount.toFixed(2)}</div> : null}
                    </div>
                    {tx.payment_status === "paid" && remaining > 0.01 && (
                      <Button size="sm" variant="outline" className="border-white/10 text-xs" onClick={() => refund(tx)} data-testid={`refund-${tx.session_id}`}>
                        <RotateCcw className="h-3 w-3 mr-1" /> Refund
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="px-6 py-12 text-center text-muted-foreground">No transactions match these filters.</div>
          )}
        </div>
      </Card>
    </div>
  );
}

function StatBig({ label, value, tone = "text-zinc-100" }) {
  return (
    <Card className="border-white/10 bg-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={"text-2xl font-display tabular-nums mt-1 " + tone}>{value}</div>
    </Card>
  );
}
